/**
 * RoutePlanner.jsx — Dispatch route planning tool.
 *
 * Data sources (authoritative):
 *   GET /tasks/       — all tasks (filtered to active, assigned to selected driver)
 *   GET /api/drivers  — real driver accounts from the backend
 *
 * Optimization:
 *   Greedy nearest-neighbor sort based on stop coordinates.
 *   Uses stored dropoffLat/dropoffLng first (from backend geocoding), then falls
 *   back to the Google Maps Geocoder for addresses that have no stored coords.
 *
 * Route rendering:
 *   Google Maps DirectionsService draws the full driving route.
 *   A stop list + total distance + estimated time are shown in the side panel.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";
import { GOOGLE_MAPS_SCRIPT_ID, GOOGLE_MAPS_LIBRARIES } from "../config/googleMaps";

import DashboardLayout   from "layouts/dashboards/DashboardLayout";
import DashboardNavbar   from "examples/Navbars/DashboardNavbar";
import MDBox             from "components/MDBox";
import MDTypography      from "components/MDTypography";
import MDButton          from "components/MDButton";
import Card              from "@mui/material/Card";
import Grid              from "@mui/material/Grid";
import Divider           from "@mui/material/Divider";
import Chip              from "@mui/material/Chip";
import Box               from "@mui/material/Box";
import Typography        from "@mui/material/Typography";
import CircularProgress  from "@mui/material/CircularProgress";
import Alert             from "@mui/material/Alert";

import { getTasks, listDrivers } from "../api/client";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";
const TERMINAL = new Set(["delivered", "completed", "canceled", "failed"]);
const DENVER = { lat: 39.7392, lng: -104.9903 };
const MIN_PLANNER_ZOOM = 9;
const MAX_PLANNER_ZOOM = 11;

/** Build a human-readable stop label for the side panel. */
function stopLabel(task, stopType) {
  const client = task.clientName || task.client || "Task";
  const type   = (task.taskType || "").toLowerCase();
  if (stopType === "dropoff") {
    return { primary: client, secondary: task.dropoffAddress || "Dropoff address not set", badge: "DROPOFF", color: "#f97316" };
  }
  return { primary: client, secondary: task.pickupAddress || "Pickup address not set", badge: type === "pickup" ? "PICKUP" : "STOP", color: "#3b82f6" };
}

/** Format seconds as "Xh Ym" or "Y min". */
function fmtDuration(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
}

function isValidCoord(lat, lng) {
  if (lat == null || lng == null) return false;
  if (isNaN(lat) || isNaN(lng)) return false;
  if (lat < -90 || lat > 90) return false;
  if (lng < -180 || lng > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
}

function getDriverCoords(driver) {
  const rawLat = driver?.last_lat ?? driver?.lastLat ?? null;
  const rawLng = driver?.last_lng ?? driver?.lastLng ?? null;
  const lat = typeof rawLat === "number" ? rawLat : parseFloat(rawLat);
  const lng = typeof rawLng === "number" ? rawLng : parseFloat(rawLng);
  return isValidCoord(lat, lng) ? { lat, lng } : null;
}

function getDriverStatus(driver) {
  return driver?.operational_status || driver?.current_status || "Available";
}

// ── Component ────────────────────────────────────────────────────────────────
export default function RoutePlanner() {
  const [drivers,        setDrivers]        = useState([]);
  const [allTasks,       setAllTasks]        = useState([]);
  const [selectedDriver, setSelectedDriver]  = useState("");
  const [driverTasks,    setDriverTasks]     = useState([]);
  const [route,          setRoute]           = useState([]);   // ordered stop objects
  const [routeSummary,   setRouteSummary]    = useState(null); // { totalMiles, totalSec }
  const [optimizing,     setOptimizing]      = useState(false);
  const [fetchLoading,   setFetchLoading]    = useState(true);
  const [fetchErr,       setFetchErr]        = useState("");
  const [routeErr,       setRouteErr]        = useState("");
  const [mapReady,       setMapReady]        = useState(false);

  const mapRef       = useRef(null);
  const mapObj       = useRef(null);   // { google, map, renderer }
  const driverMarkerRef = useRef(null);
  const dragFromRef  = useRef(null);   // index of the stop being dragged
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const selectedDriverObj = drivers.find(
    (d) => ((d.name || d.email || "").toLowerCase() === selectedDriver.toLowerCase())
  );

  // ── Load backend data ──────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setFetchLoading(true);
    try {
      const [tRes, dRes] = await Promise.all([getTasks(), listDrivers()]);
      if (tRes.ok) {
        const d = await tRes.json();
        setAllTasks(Array.isArray(d) ? d : []);
      } else {
        setFetchErr(`Tasks: HTTP ${tRes.status}`);
      }
      if (dRes.ok) {
        const d = await dRes.json();
        setDrivers(Array.isArray(d) ? d : []);
      } else {
        setFetchErr((e) => e + ` Drivers: HTTP ${dRes.status}`);
      }
    } catch (e) {
      setFetchErr(e.message);
    } finally {
      setFetchLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Filter tasks for selected driver ──────────────────────────────────────
  useEffect(() => {
    if (!selectedDriver) {
      setDriverTasks([]);
      setRoute([]);
      setRouteSummary(null);
      return;
    }
    const filtered = allTasks.filter((t) => {
      const s = (t.status || "").toLowerCase();
      if (TERMINAL.has(s)) return false;
      // Match on any driver name field stored by the backend
      const names = [t.driver, t.assignedDriver, t.driverName].filter(Boolean).map((n) => n.toLowerCase());
      return names.includes(selectedDriver.toLowerCase());
    });
    setDriverTasks(filtered);
    setRoute([]);
    setRouteSummary(null);
    setRouteErr("");
  }, [selectedDriver, allTasks]);

  // ── Map init ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!MAPS_KEY || !mapRef.current) return;
    let mounted = true;
    const loader = new Loader({
      id: GOOGLE_MAPS_SCRIPT_ID,
      apiKey: MAPS_KEY,
      version: "weekly",
      libraries: GOOGLE_MAPS_LIBRARIES,
    });
    loader.load().then((google) => {
      if (!mounted || !mapRef.current) return;
      const map = new google.maps.Map(mapRef.current, {
        center: DENVER,
        zoom: 9,
        mapTypeControl: false,
        streetViewControl: false,
      });
      const renderer = new google.maps.DirectionsRenderer({
        suppressMarkers: false,
        preserveViewport: true,
      });
      renderer.setMap(map);
      mapObj.current = { google, map, renderer };
      setMapReady(true);
    });
    return () => {
      mounted = false;
      setMapReady(false);
    };
  }, []);

  // ── Geocode helper (stored coords first, then Google fallback) ─────────────
  async function resolveCoords(task) {
    // Delivery tasks: use stored dropoffLat/Lng if available
    const type = (task.taskType || "").toLowerCase();
    const isDelivery = type === "delivery" || !type;

    if (isDelivery && task.dropoffLat != null && task.dropoffLng != null) {
      return {
        lat:       task.dropoffLat,
        lng:       task.dropoffLng,
        address:   task.dropoffAddress || "",
        stopType:  "dropoff",
      };
    }

    // Pickup tasks: geocode pickupAddress
    const addr = type === "pickup"
      ? (task.pickupAddress || "")
      : (task.dropoffAddress || task.pickupAddress || "");

    if (!addr) return null;

    const google = window.google;
    if (!google) return null;

    return new Promise((resolve) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: addr }, (results, status) => {
        if (status === "OK" && results?.[0]?.geometry?.location) {
          resolve({
            lat:      results[0].geometry.location.lat(),
            lng:      results[0].geometry.location.lng(),
            address:  addr,
            stopType: type === "pickup" ? "pickup" : "dropoff",
          });
        } else {
          resolve(null);
        }
      });
    });
  }

  // ── Drag-and-drop reorder ──────────────────────────────────────────────────
  /**
   * Re-draws the Directions route on the map for a given ordered stop list.
   * Called after both optimization and manual drag-reorder.
   */
  const fitPlannerViewport = ({ ordered = [], routeResult = null, driver = null } = {}) => {
    if (!mapObj.current) return;
    const { google, map } = mapObj.current;
    const bounds = new google.maps.LatLngBounds();

    if (routeResult?.routes?.[0]?.overview_path?.length) {
      routeResult.routes[0].overview_path.forEach((p) => bounds.extend(p));
    } else {
      ordered.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
    }

    const driverCoords = getDriverCoords(driver);
    if (driverCoords) bounds.extend(driverCoords);

    if (bounds.isEmpty()) {
      map.setCenter(DENVER);
      map.setZoom(9);
      return;
    }

    map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
    google.maps.event.addListenerOnce(map, "idle", () => {
      const z = map.getZoom();
      if (z > MAX_PLANNER_ZOOM) map.setZoom(MAX_PLANNER_ZOOM);
      else if (z < MIN_PLANNER_ZOOM) map.setZoom(MIN_PLANNER_ZOOM);
    });
  };

  const drawRoute = (ordered) => {
    if (!mapObj.current || ordered.length < 2) return;
    const { google, renderer } = mapObj.current;
    const directionsService = new google.maps.DirectionsService();
    const waypoints = ordered.slice(1, -1).map((s) => ({
      location: { lat: s.lat, lng: s.lng },
      stopover: true,
    }));
    directionsService.route(
      {
        origin:      { lat: ordered[0].lat,                     lng: ordered[0].lng },
        destination: { lat: ordered[ordered.length - 1].lat,    lng: ordered[ordered.length - 1].lng },
        waypoints,
        travelMode:  google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (status === "OK") {
          renderer.setDirections(result);
          const legs      = result.routes[0].legs;
          const totalMiles = legs.reduce((s, l) => s + l.distance.value, 0) / 1609.34;
          const totalSec   = legs.reduce((s, l) => s + l.duration.value, 0);
          setRouteSummary({ totalMiles, totalSec });
          fitPlannerViewport({ ordered, routeResult: result, driver: selectedDriverObj });
        }
      }
    );
  };

  useEffect(() => {
    if (!mapReady || !mapObj.current) return;
    if (driverMarkerRef.current) {
      driverMarkerRef.current.setMap(null);
      driverMarkerRef.current = null;
    }

    const coords = getDriverCoords(selectedDriverObj);
    if (!coords) return;

    const { google, map } = mapObj.current;
    const marker = new google.maps.Marker({
      position: coords,
      map,
      title: `${selectedDriverObj?.name || selectedDriverObj?.email || "Driver"} — ${getDriverStatus(selectedDriverObj)}`,
      icon: {
        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        fillColor: "#0ea5e9",
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: 2,
        scale: 6,
      },
      zIndex: 10,
    });
    driverMarkerRef.current = marker;

    if (route.length === 0) {
      fitPlannerViewport({ driver: selectedDriverObj });
    }

    return () => {
      marker.setMap(null);
      if (driverMarkerRef.current === marker) driverMarkerRef.current = null;
    };
  }, [selectedDriverObj, route.length, mapReady]);

  const handleDropStop = (toIdx) => {
    const fromIdx = dragFromRef.current;
    setDragOverIdx(null);
    dragFromRef.current = null;
    if (fromIdx === null || fromIdx === toIdx) return;

    const newRoute = [...route];
    const [moved]  = newRoute.splice(fromIdx, 1);
    newRoute.splice(toIdx, 0, moved);

    setRoute(newRoute);
    drawRoute(newRoute);
  };

  // ── Optimize & draw route ──────────────────────────────────────────────────
  const handleOptimize = async () => {
    if (!mapObj.current) {
      setRouteErr("Map not ready — wait a moment and try again.");
      return;
    }
    setOptimizing(true);
    setRouteErr("");
    setRoute([]);
    setRouteSummary(null);
    mapObj.current.renderer.setDirections({ routes: [] });

    const { google, renderer } = mapObj.current;

    // 1. Resolve coordinates for each task stop
    const resolved = (
      await Promise.all(
        driverTasks.map(async (task) => {
          const coords = await resolveCoords(task);
          if (!coords) return null;
          return { task, ...coords };
        })
      )
    ).filter(Boolean);

    if (resolved.length === 0) {
      setRouteErr(
        "No stops could be located. Ensure tasks have delivery addresses, or fix location data in the Courier Dashboard."
      );
      setOptimizing(false);
      return;
    }

    if (resolved.length === 1) {
      // Single stop — just center the map, no route line needed
      setRoute(resolved);
      setRouteSummary({ totalMiles: 0, totalSec: 0 });
      fitPlannerViewport({ ordered: resolved, driver: selectedDriverObj });
      setOptimizing(false);
      return;
    }

    // 2. Greedy nearest-neighbor sort
    const ordered = [];
    let remaining = [...resolved];
    let current = remaining.shift();
    ordered.push(current);
    while (remaining.length > 0) {
      const currentStop = current;
      remaining.sort((a, b) => {
        const dA = Math.hypot(a.lat - currentStop.lat, a.lng - currentStop.lng);
        const dB = Math.hypot(b.lat - currentStop.lat, b.lng - currentStop.lng);
        return dA - dB;
      });
      current = remaining.shift();
      ordered.push(current);
    }

    // 3. Draw the initial optimized route then hand off to setRoute
    setRoute(ordered);

    const directionsService = new google.maps.DirectionsService();
    const waypoints = ordered.slice(1, -1).map((s) => ({
      location: { lat: s.lat, lng: s.lng },
      stopover: true,
    }));

    directionsService.route(
      {
        origin:      { lat: ordered[0].lat,                     lng: ordered[0].lng },
        destination: { lat: ordered[ordered.length - 1].lat,    lng: ordered[ordered.length - 1].lng },
        waypoints,
        travelMode:  google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false, // already sorted by greedy nearest-neighbor
      },
      (result, status) => {
        if (status === "OK") {
          renderer.setDirections(result);
          const legs       = result.routes[0].legs;
          const totalMiles = legs.reduce((s, l) => s + l.distance.value, 0) / 1609.34;
          const totalSec   = legs.reduce((s, l) => s + l.duration.value, 0);
          setRouteSummary({ totalMiles, totalSec });
          fitPlannerViewport({ ordered, routeResult: result, driver: selectedDriverObj });
        } else {
          // Fallback: drop markers manually if Directions fails
          renderer.setDirections({ routes: [] });
          ordered.forEach((s, i) => {
            new google.maps.Marker({
              position: { lat: s.lat, lng: s.lng },
              map: mapObj.current.map,
              label:    String(i + 1),
              title:    s.task.clientName || s.address,
            });
          });
          setRouteErr(`Route line unavailable (Directions API: ${status}). Stops are pinned on the map.`);
        }
        setOptimizing(false);
      }
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={3}>
        <Grid container spacing={3}>

          {/* ── Left panel ─────────────────────────────────────────────── */}
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, borderRadius: "1rem" }}>
              <MDTypography variant="h6" fontWeight="bold" mb={3}>
                Route Controls
              </MDTypography>

              {fetchErr && (
                <Alert severity="error" sx={{ mb: 2 }}>{fetchErr}</Alert>
              )}

              {/* Driver selector */}
              <MDBox mb={2}>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  SELECT DRIVER
                </MDTypography>
                {fetchLoading ? (
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Loading drivers…</Typography>
                  </Box>
                ) : (
                  <select
                    value={selectedDriver}
                    onChange={(e) => setSelectedDriver(e.target.value)}
                    style={{
                      width: "100%", borderRadius: 8, border: "1px solid #d1d5db",
                      padding: "8px 10px", fontSize: 14, marginTop: 4,
                      outline: "none", background: "#fff",
                    }}
                  >
                    <option value="">Choose a driver…</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.name || d.email}>
                        {d.name || d.email}
                        {getDriverStatus(d) ? ` — ${getDriverStatus(d)}` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </MDBox>

              {/* Driver status context */}
              {selectedDriverObj && (
                <Box
                  sx={{
                    mb: 2, p: 1.25, bgcolor: "#f8fafc", borderRadius: 2,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
                    CURRENT STATUS
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      size="small"
                      label={getDriverStatus(selectedDriverObj)}
                      color={getDriverStatus(selectedDriverObj) !== "Available" ? "info" : "default"}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {driverTasks.length} active task{driverTasks.length !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* No tasks message */}
              {selectedDriver && !fetchLoading && driverTasks.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No active tasks assigned to this driver. Assign tasks in the Courier Dashboard first.
                </Alert>
              )}

              {/* Pending stops preview */}
              {driverTasks.length > 0 && route.length === 0 && (
                <MDBox mb={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.5}>
                    ASSIGNED STOPS ({driverTasks.length})
                  </Typography>
                  {driverTasks.map((t, i) => {
                    const type = (t.taskType || "delivery").toLowerCase();
                    const addr = type === "pickup" ? t.pickupAddress : (t.dropoffAddress || t.pickupAddress);
                    const hasCoords = t.dropoffLat != null && t.dropoffLng != null;
                    return (
                      <Box key={t.id} sx={{ display: "flex", gap: 1.5, mb: 1, alignItems: "flex-start" }}>
                        <Box sx={{
                          width: 20, height: 20, borderRadius: "50%", bgcolor: "grey.300",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.25,
                        }}>
                          <Typography variant="caption" fontWeight="bold" fontSize={10}>{i + 1}</Typography>
                        </Box>
                        <Box minWidth={0}>
                          <Typography variant="caption" fontWeight="bold" display="block" noWrap>
                            {t.clientName || t.client || "Task"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {addr || "No address"}
                          </Typography>
                          {!hasCoords && (
                            <Typography variant="caption" color="warning.main" display="block">
                              ⚠ No stored coords — will geocode
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </MDBox>
              )}

              {/* Optimize button */}
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                onClick={handleOptimize}
                disabled={!selectedDriver || driverTasks.length === 0 || optimizing || fetchLoading}
              >
                {optimizing
                  ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Calculating…</>
                  : `Optimize Route (${driverTasks.length} stop${driverTasks.length !== 1 ? "s" : ""})`}
              </MDButton>

              {routeErr && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>{routeErr}</Alert>
              )}

              {/* Route result */}
              {route.length > 0 && (
                <MDBox mt={3}>
                  <Divider />
                  <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} mb={1.5}>
                    <MDTypography variant="h6" fontWeight="bold">
                      Optimized Route
                    </MDTypography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      Drag to reorder
                    </Typography>
                  </Box>

                  {route.map((s, i) => {
                    const lbl      = stopLabel(s.task, s.stopType);
                    const isDragOver = dragOverIdx === i;
                    return (
                      <Box
                        key={`${s.task?.id ?? i}-${i}`}
                        draggable
                        onDragStart={() => { dragFromRef.current = i; }}
                        onDragOver={(e) => { e.preventDefault(); setDragOverIdx(i); }}
                        onDragLeave={() => setDragOverIdx(null)}
                        onDrop={() => handleDropStop(i)}
                        onDragEnd={() => { setDragOverIdx(null); dragFromRef.current = null; }}
                        sx={{
                          display:     "flex",
                          gap:         1.5,
                          mb:          1.5,
                          alignItems:  "flex-start",
                          cursor:      "grab",
                          userSelect:  "none",
                          p:           1,
                          borderRadius: 1.5,
                          border:      isDragOver ? "2px dashed #3b82f6" : "2px solid transparent",
                          bgcolor:     isDragOver ? "#eff6ff" : "transparent",
                          transition:  "background 0.12s, border-color 0.12s",
                          "&:hover":   { bgcolor: "#f8fafc" },
                          "&:active":  { cursor: "grabbing" },
                        }}
                      >
                        {/* Drag handle */}
                        <Box
                          sx={{
                            display:    "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            gap:        "3px",
                            flexShrink: 0,
                            mt:         0.5,
                            opacity:    0.35,
                          }}
                        >
                          {[0,1,2].map((dot) => (
                            <Box key={dot} sx={{ display: "flex", gap: "3px" }}>
                              <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#64748b" }} />
                              <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#64748b" }} />
                            </Box>
                          ))}
                        </Box>

                        {/* Stop number badge */}
                        <Box sx={{
                          width: 26, height: 26, borderRadius: "50%",
                          bgcolor: lbl.color, display: "flex", alignItems: "center",
                          justifyContent: "center", flexShrink: 0,
                        }}>
                          <Typography variant="caption" color="white" fontWeight="bold" fontSize={11}>
                            {i + 1}
                          </Typography>
                        </Box>

                        {/* Stop info */}
                        <Box minWidth={0} flex={1}>
                          <Box display="flex" alignItems="center" gap={0.75} mb={0.1}>
                            <Typography variant="caption" fontWeight="bold" fontSize={12} noWrap>
                              {lbl.primary}
                            </Typography>
                            <Chip
                              size="small"
                              label={lbl.badge}
                              sx={{
                                height: 16, fontSize: 9, fontWeight: 700,
                                bgcolor: lbl.color, color: "#fff",
                                "& .MuiChip-label": { px: 0.75 },
                                flexShrink: 0,
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {lbl.secondary}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}

                  {routeSummary && (
                    <Box
                      sx={{ mt: 2, p: 2, bgcolor: "#f1f5f9", borderRadius: 2 }}
                    >
                      <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block">
                        ROUTE SUMMARY
                      </Typography>
                      <Box display="flex" gap={3} mt={0.75}>
                        <Box>
                          <Typography variant="h5" fontWeight="bold" color="info.main">
                            {routeSummary.totalMiles.toFixed(1)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Miles</Typography>
                        </Box>
                        {routeSummary.totalSec > 0 && (
                          <Box>
                            <Typography variant="h5" fontWeight="bold" color="info.main">
                              {fmtDuration(routeSummary.totalSec)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">Est. Time</Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography variant="h5" fontWeight="bold" color="info.main">
                            {route.length}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Stops</Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </MDBox>
              )}
            </Card>
          </Grid>

          {/* ── Map panel ──────────────────────────────────────────────── */}
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: "1.25rem", overflow: "hidden", height: "100%", minHeight: 560, position: "relative" }}>
              <div ref={mapRef} style={{ width: "100%", height: "100%", minHeight: 560 }} />
              {!MAPS_KEY && (
                <Box
                  sx={{
                    position: "absolute", inset: 0, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    background: "#f8fafc", flexDirection: "column", gap: 1,
                  }}
                >
                  <Typography fontWeight={700} color="text.secondary">Map unavailable</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Set REACT_APP_GOOGLE_MAPS_KEY in your .env file.
                  </Typography>
                </Box>
              )}
            </Card>
          </Grid>

        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
